import {
  FINDING_GROUP_ORDER,
  orderFindings,
} from '@/features/agentic-insights/components/data/groupFindings';
import type { PersonaView } from '@/features/agentic-insights/components/data/resolvePersonaView';
import type { Finding, FindingGroup } from '@/features/agentic-insights/components/data/types';
import { UNASSIGNED_OBJECTIVE } from './unassignedObjective';

export interface FindingEntry {
  finding: Finding;
  /** 1-based within its own group, which is how a reader refers to it out loud. */
  ordinal: number;
  group: FindingGroup;
}

/**
 * The findings a reader can actually read, as numbered entries grouped by what the contract says
 * they are — risk, opportunity, on track, data readiness — in the contract's own severity order.
 *
 * The four groups are used as they are rather than collapsed into a coarser split. `dataReadiness`
 * in particular exists so that a finding about what the data cannot yet measure stops being filed as
 * a risk; folding it back in with the risks defeats the reason it is a separate group.
 *
 * Withheld findings are dropped rather than numbered: a gap in a numbered list reads as a mistake,
 * and the count of what a role cannot see is stated in the header sentence instead. Ordering inside
 * a group is the agent's own decision order, so the numbers mean something.
 */
export const buildFindingEntries = (
  findings: Finding[],
  views: Map<string, PersonaView>,
): FindingEntry[] => {
  const readable = findings.filter((finding) => views.get(finding.id)?.kind === 'voiced');

  return FINDING_GROUP_ORDER.flatMap((group) =>
    orderFindings(readable.filter((finding) => finding.group === group)).map((finding, index) => ({
      finding,
      ordinal: index + 1,
      group,
    })),
  );
};

const findingMatchesObjective = (finding: Finding, objective: string | undefined): boolean => {
  if (objective === undefined) return true;
  if (objective === UNASSIGNED_OBJECTIVE) return finding.objectiveIds.length === 0;
  return finding.objectiveIds.includes(objective);
};

/**
 * Filtering happens before numbering, so the numbers a reader sees always match the list in front of
 * them. The alternative — stable global numbers with gaps — reads as a filtered spreadsheet rather
 * than as an argument.
 */
export const filterFindingEntries = (
  entries: FindingEntry[],
  objective: string | undefined,
): FindingEntry[] => {
  const seenInGroup = new Map<FindingGroup, number>();

  return entries
    .filter((entry) => findingMatchesObjective(entry.finding, objective))
    .map((entry) => {
      const ordinal = (seenInGroup.get(entry.group) ?? 0) + 1;
      seenInGroup.set(entry.group, ordinal);
      return { ...entry, ordinal };
    });
};

export const countWithheld = (findings: Finding[], views: Map<string, PersonaView>): number =>
  findings.filter((finding) => views.get(finding.id)?.kind === 'withheld').length;
